import { useEffect, useState } from 'react';
import { useAuth, ShellAuthProvider } from '@aimsos/auth';
import { DashboardsApp } from '@aimsos/dashboards';
import { registerNamespace, i18n } from '../i18n/bootstrap.js';

/**
 * Mounts the Dashboards application inside the Core Shell.
 * Mirrors the same embedding pattern as UserMgmtEmbed.
 *
 * Reads from the unified useAuth() (backed by our own backend JWT) instead
 * of useAuth0() directly, so this still mounts correctly on a
 * {slug}.{rootDomain} subdomain landed on via a cross-subdomain handoff,
 * where Auth0 SDK itself has no session.
 */
const DashboardsEmbed = () => {
  const { user, token, isAuthenticated, getAccessTokenSilently } = useAuth();
  const [i18nReady, setI18nReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    import('@aimsos/dashboards/i18n/register')
      .then(({ registerDashboardsI18n }) => {
        registerDashboardsI18n(registerNamespace);
        return i18n.loadNamespaces('dashboards');
      })
      .then(() => {
        if (!cancelled) setI18nReady(true);
      })
      .catch((err) => console.error('[DashboardsEmbed] i18n:', err));
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;
    window.__AIMS_SHELL__ = {
      mounted: true,
      // The Dashboard API is its own gateway, not the platform one — the
      // widget module lives behind `{stage}-aimsos-dashboard-api`. Injected
      // the same way Data Studio and Governance get theirs, because an
      // embedded app cannot read the shell's `import.meta.env`.
      apiBaseUrlDashboard: import.meta.env.VITE_API_BASE_URL_DASHBOARD,
      // The Widget Builder's entity and field pickers are served by the
      // data-model aggregate, which lives on the Data Studio gateway.
      apiBaseUrlDS: import.meta.env.VITE_API_BASE_URL_DS,
    };
    return () => {
      delete window.__AIMS_SHELL__;
    };
  }, [isAuthenticated]);

  if (!isAuthenticated || !token || !i18nReady) return null;

  return (
    <ShellAuthProvider
      token={token}
      user={user}
      isAuthenticated={isAuthenticated}
      getAccessTokenSilently={getAccessTokenSilently}
    >
      <DashboardsApp />
    </ShellAuthProvider>
  );
};

export default DashboardsEmbed;
